import { watch } from "vue";
import { createI18n } from "vue-i18n";
import pt from "../locales/pt.json";
import en from "../locales/en.json";
import fr from "../locales/fr.json";

export const LOCALE_STORAGE_KEY = "incubator.locale";
export const supportedLocales = [
  { code: "pt", label: "Português", tag: "pt-PT" },
  { code: "en", label: "English", tag: "en-GB" },
  { code: "fr", label: "Français", tag: "fr-FR" },
];

export function initialLocale(storage) {
  try {
    const saved = storage.getItem(LOCALE_STORAGE_KEY);
    return supportedLocales.some(({ code }) => code === saved) ? saved : "pt";
  } catch {
    return "pt";
  }
}

let locale = "pt";
if (typeof window !== "undefined") {
  // Accessing localStorage itself can throw when browser storage is disabled.
  try { locale = initialLocale(window.localStorage); } catch { /* use Portuguese */ }
}

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale,
  fallbackLocale: "pt",
  messages: { pt, en, fr },
});

export function setLocale(code) {
  if (supportedLocales.some((item) => item.code === code)) {
    i18n.global.locale.value = code;
  }
}

export function localeTag() {
  return supportedLocales.find(({ code }) => code === i18n.global.locale.value).tag;
}

export function formatDate(value, options = { day: "2-digit", month: "short", year: "numeric" }) {
  if (!value) return i18n.global.t("common.noDate");
  // Date-only values are calendar dates, not UTC instants.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(localeTag(), options).format(date);
}

// Local messages are stored as keys so an open alert changes language too.
// Unknown server messages remain intact; no user data is sent for translation.
const knownMessages = new Map([
  ...Object.entries(pt.auth).map(([key, value]) => [value, `auth.${key}`]),
  ...Object.entries(pt.chat).map(([key, value]) => [value, `chat.${key}`]),
  ["As senhas não coincidem.", "auth.passwordMismatch"],
  ["Senha alterada com sucesso.", "auth.passwordChanged"],
  ["Senha alterada com sucesso!", "auth.passwordChanged"],
  ["Erro ao alterar senha.", "auth.passwordError"],
  ["Erro ao enviar registro.", "auth.requestError"],
  ["Registro enviado com sucesso! Aguarde a administração.", "auth.requestSent"],
  ["Login realizado com sucesso!", "auth.loginSuccess"],
  ["Erro ao tentar logar. Tente novamente.", "auth.loginError"],
]);

export function localizeMessage(value) {
  if (!value) return "";
  const key = knownMessages.get(value) || value;
  return i18n.global.te(key, "pt") ? i18n.global.t(key) : value;
}

export function installLocaleEffects(router) {
  const update = () => {
    const code = i18n.global.locale.value;
    document.documentElement.lang = localeTag();
    const titleKey = router.currentRoute.value.meta.titleKey;
    const title = titleKey ? i18n.global.t(titleKey) : router.currentRoute.value.meta.title;
    document.title = title ? `${title} - INCUBATOR` : "INCUBATOR";
    try { window.localStorage.setItem(LOCALE_STORAGE_KEY, code); } catch { /* language still works */ }
  };
  watch([i18n.global.locale, () => router.currentRoute.value], update, { immediate: true });
}

export default i18n;
